// ---------------------------------------------------------------------------
// Content hooks
// ---------------------------------------------------------------------------
// react-query wrappers around the data layer in src/lib/api.js. Components use
// these rather than calling Firestore or the api module directly, so caching,
// retries and loading/error state are handled in one place.
//
// The QueryClient (src/index.js) is configured with staleTime 60s and
// refetchOnWindowFocus false — appropriate for marketing content that changes
// only when an admin edits it.
// ---------------------------------------------------------------------------
import { useQuery, useMutation } from "@tanstack/react-query";

import { SITE, buildWhatsappLink, digitsOnly } from "@/data/site";

import {
  getServices,
  getService,
  getProgrammes,
  getTestimonials,
  getBlogPosts,
  getBlogPost,
  getSeo,
  getSettings,
  getCodeInjection,
  getTaxonomy,
  submitEnquiry,
} from "@/lib/api";

// Central key registry — keeps cache keys consistent and makes targeted
// invalidation from the admin panel straightforward.
export const queryKeys = {
  services: ["services"],
  service: (slug) => ["service", slug],
  programmes: ["programmes"],
  testimonials: ["testimonials"],
  blogPosts: ["blogPosts"],
  blogPost: (slug) => ["blogPost", slug],
  seo: (path) => ["seo", path],
  settings: ["settings"],
  codeInjection: ["codeInjection"],
  taxonomy: ["taxonomy"],
};

// Static, code-owned data — never refetch.
export function useServices() {
  return useQuery({
    queryKey: queryKeys.services,
    queryFn: getServices,
    staleTime: Infinity,
  });
}

export function useService(slug) {
  return useQuery({
    queryKey: queryKeys.service(slug),
    queryFn: () => getService(slug),
    enabled: Boolean(slug),
    staleTime: Infinity,
  });
}

export function useProgrammes() {
  return useQuery({
    queryKey: queryKeys.programmes,
    queryFn: getProgrammes,
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: queryKeys.testimonials,
    queryFn: getTestimonials,
  });
}

export function useBlogPosts() {
  return useQuery({
    queryKey: queryKeys.blogPosts,
    queryFn: getBlogPosts,
  });
}

export function useBlogPost(slug) {
  return useQuery({
    queryKey: queryKeys.blogPost(slug),
    queryFn: () => getBlogPost(slug),
    enabled: Boolean(slug),
  });
}

/**
 * Per-page SEO overrides. Kept quiet on failure: if this request fails the
 * page must still render with its built-in defaults, so no retry storm and no
 * error surfaced to the user.
 */
export function useSeo(path) {
  return useQuery({
    queryKey: queryKeys.seo(path),
    queryFn: () => getSeo(path),
    enabled: Boolean(path),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Live site details for the public pages.
 *
 * Returns the saved settings merged over the defaults in src/data/site.js, so
 * `site` is always a complete object — never undefined mid-load and never
 * missing a field. `waLink` builds a WhatsApp URL from whatever number is
 * currently saved, which is why no component should import whatsappLink
 * directly any more.
 *
 * `socials` lists only the networks that actually have a link, so the footer
 * renders nothing at all until the client fills one in.
 */
export function useSite() {
  const { data } = useSettings();
  const site = data ? { ...SITE, ...data } : SITE;

  const socials = [
    { key: "facebook", label: "Facebook", url: site.facebook },
    { key: "instagram", label: "Instagram", url: site.instagram },
    { key: "tiktok", label: "TikTok", url: site.tiktok },
  ].filter((s) => typeof s.url === "string" && s.url.trim().length > 0);

  return {
    site,
    socials,
    waLink: (message) => buildWhatsappLink(site, message),
    telLink: `tel:${digitsOnly(site.mobileRaw || site.mobile)}`,
  };
}

export function useCodeInjection() {
  return useQuery({
    queryKey: queryKeys.codeInjection,
    queryFn: getCodeInjection,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
}

export function useTaxonomy() {
  return useQuery({
    queryKey: queryKeys.taxonomy,
    queryFn: getTaxonomy,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Submit an enquiry.
 *
 * No retry: a retried write would create a duplicate enquiry and fire a second
 * pair of emails. The form surfaces the error and lets the user decide.
 */
export function useSubmitEnquiry() {
  return useMutation({
    mutationFn: submitEnquiry,
    retry: false,
  });
}
